import Vue from 'vue'
import VueRouter from 'vue-router'
import { find } from 'lodash'
import camelcaseKeys from 'camelcase-keys'
import { canNavigate } from '@/plugins/acl/routeProtection'
import UserApi from '@/api/waiter/User'
import { isUnderMaintenance, isMaintenancePath } from '@/config/maintenance'
import admin from './admin'
import waiter from './waiter'
import cast from './cast'
import { getHomeRoute } from './homeRoute'
import { createFloorFixScroll } from './floorFixScroll'
import { setCurrentClub } from '@/views/composable/useCurrentData'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    redirect: () => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'))
      const currentClub = JSON.parse(localStorage.getItem('currentClub'))

      return getHomeRoute(currentUser, currentClub)
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/Error404.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
    },
  },
  {
    path: '/login',
    name: 'auth-login',
    component: () => import('@/views/Login.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/forgot-password',
    name: 'auth-forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@/views/Maintenance.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
    },
  },
  ...admin,
  ...waiter,
  ...cast,
  {
    path: '*',
    redirect: 'error-404',
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
})

const getUsersOwnerships = async () => {
  let availableClubs = []

  const res = await UserApi.getUsersOwnerships()
  if (res?.data) availableClubs = [...res.data]

  return availableClubs
}

const updateCurrentClub = async id => {
  const currentClub = JSON.parse(localStorage.getItem('currentClub') || '{}')
  if (+id === +currentClub.id) return

  const res = await UserApi.updateUserCurrentClubId({ clubId: id })
  if (res?.data) setCurrentClub(camelcaseKeys(res.data))
}

router.beforeEach(async (to, _, next) => {
  const isLoggedIn = localStorage.getItem('currentUser')
    && localStorage.getItem('currentClub')
    && localStorage.getItem('userAbilities')

  if (!canNavigate(to)) {
    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: 'auth-login' })

    // If logged in => not authorized
    // 表示非表示の制御はcaslでやってるけど、アクセスはここでやってる
    return next('/')
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    next('/')
  }

  // メンテナンスチェック
  if (isUnderMaintenance() && isMaintenancePath(to.path)) {
    return next({ name: 'maintenance' })
  }

  // club_slugがcurrentClubと違う場合はownershipsを取得してcurrentClubを更新する
  // 直リンクでアクセスした時などclub_slugとcurrentClubが異なる場合があるため
  // e.g.
  // 複数店舗経営のスーパーアドミンが店舗Aにログインしている状態で店舗BのURL(日報)を共有されたときとか
  const currentClub = JSON.parse(localStorage.getItem('currentClub') || '{}')
  const currentParamsClubSlug = to.params.club_slug
  if (currentClub.slug !== currentParamsClubSlug) {
    const availableClubs = await getUsersOwnerships()
    const clubId = find((availableClubs || []), { slug: currentParamsClubSlug })?.id

    if (clubId) {
      await updateCurrentClub(clubId)
    }
  }

  return next()
})

// フロアビューのDrawerがhtml/bodyのoverflowをロックしたまま遷移する問題の対策。
// 注意: Floor.vue の resetBodyOverflow と対象が同じ Vuetify ロックだが、
// こちらはルート遷移時の "後始末" で、resetBodyOverflow はダイアログ閉じ直後
// の "前始末" として補完関係にある。Vuetify 改修で根本対処すべき tech debt。
let floorFixStyle = null
const floorFix = createFloorFixScroll()
router.afterEach((to, from) => {
  // フロアから離れる時にスクロール修正を適用
  if (from.name === 'waiter-floor' && to.name !== 'waiter-floor') {
    if (!floorFixStyle) {
      floorFixStyle = document.createElement('style')
      floorFixStyle.textContent = 'html.floor-fix-scroll .v-application--wrap { max-height: 100vh !important; overflow: auto !important; }'
      document.head.appendChild(floorFixStyle)
    }
    document.documentElement.classList.add('floor-fix-scroll')

    // 遷移先でDrawerが開かれたらクラスを外す（html styleの変更を監視）。
    // 少し遅延してから監視開始（遷移直後の変更を無視するため）
    floorFix.start(document.documentElement, () => {
      document.documentElement.classList.remove('floor-fix-scroll')
    })
  }

  // フロアに戻ったらクラスを外す
  if (to.name === 'waiter-floor') {
    document.documentElement.classList.remove('floor-fix-scroll')
    floorFix.cancel()
  }
})

export default router
